// assets/js/utils/phone-shape.js
/**
 * Is a stored phone number the wrong SHAPE — as opposed to the guest simply
 * not being on WhatsApp? (task #654)
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * On the Olivia & Yarden wedding (2026-09-03), 14 guests had their WhatsApp
 * send fail and were emailed instead. The host was told those numbers were
 * "not on WhatsApp" — Meta's 131026. Six of the fourteen were on WhatsApp
 * perfectly well; the number we dialled was mangled:
 *
 *   +4444795664033   the UK country code twice
 *   +0526771977      an Israeli local number, trunk 0 kept, no country code
 *   +9725878391919   +972 with a 10-digit national part (Israeli mobiles are 9)
 *   +97255773648     +972 with an 8-digit mobile (again, 9)
 *   +44504216828     an Israeli mobile wearing a UK prefix
 *   +66532387816     an Israeli mobile wearing a Thai prefix
 *
 * We did not reach those guests, we charged for the attempt, and we reported a
 * fact about the GUEST that was really a fact about our own data — so the host
 * had no reason to go and fix the number.
 *
 * ── The one rule that matters here ───────────────────────────────────────
 * PRECISION OVER RECALL. Telling a host their number looks wrong when it is
 * fine is worse than staying quiet: they will stop believing the warning, and
 * the warning is the whole product here. So every check below only fires when
 * the number CANNOT be valid, and an unrecognised country code returns null
 * rather than a guess. Four of the six above are caught by shape alone; the
 * last two are only catchable with a hint about which country the event's
 * guests are in, which `expectedCountry` supplies.
 */

// National-number lengths we are confident about, by country calling code.
// Deliberately short: a country whose national length genuinely varies (DE, at
// 6–11 digits) is ABSENT, which means "never flagged on length".
const NATIONAL_LENGTHS = {
  '1': [10],          // US / Canada
  '972': [8, 9],      // Israel — 9 for mobile, 8 for most landlines
  '44': [9, 10],      // UK
  '33': [9],          // France
  '34': [9],          // Spain
  '351': [9],         // Portugal
  '61': [9],          // Australia
  '91': [10],         // India
  '52': [10],         // Mexico
  '55': [10, 11],     // Brazil
  '66': [9]           // Thailand
};

// Mobile prefixes, where the country's mobile length is stricter than its
// general one. Israel is the case that matters: 05X mobiles are always 9
// national digits, so an 8-digit number starting 5 cannot be a mobile.
const MOBILE_RULES = {
  '972': { startsWith: ['5'], length: 9 }
};

// Longest first, so '972' is tried before '9' would be.
const CODES = Object.keys(NATIONAL_LENGTHS).sort((a, b) => b.length - a.length);

/** Digits only, with a leading + stripped. Keeps a leading 0 — that is a signal. */
function digitsOf(phone) {
  return String(phone == null ? '' : phone).replace(/[^\d]/g, '');
}

/**
 * What is wrong with the shape of this number, if anything.
 *
 * @param {string} phone
 * @param {string} [expectedCountry] calling code the event's guests are mostly
 *        in (e.g. '972'). Only used for the wrong-country check, which cannot
 *        be made without it.
 * @returns {{code: string, detail: string}|null} null when nothing is provably
 *          wrong — including for numbers we simply cannot judge.
 */
function phoneShapeProblem(phone, expectedCountry) {
  const raw = String(phone == null ? '' : phone).trim();
  if (!raw) return null;
  const digits = digitsOf(raw);
  if (!digits) return null;

  // A number too short to be anything. 7 is below every national minimum.
  if (digits.length < 8) {
    return { code: 'too-short', detail: `only ${digits.length} digits` };
  }

  // E.164 never starts with 0 — a leading zero means a local number was stored
  // with its trunk prefix and no country code at all (+0526771977).
  if (digits[0] === '0') {
    return { code: 'leading-zero', detail: 'starts with 0 and has no country code' };
  }

  const cc = CODES.find(c => digits.startsWith(c));
  if (!cc) return null;                       // unknown country: never guess
  let national = digits.slice(cc.length);

  // "Country code + trunk 0" is NOT a defect, however wrong it looks. Meta
  // strips the trunk 0 itself and +972 0 5xxxxxxxx delivers perfectly well —
  // flagging it was the entire false-positive problem of an earlier scan of
  // this same data. Strip it before measuring, exactly as Meta does.
  if (national.length > 1 && national[0] === '0') national = national.slice(1);

  // Mexico writes mobiles as +52 1 + 10 digits as often as +52 + 10, and both
  // resolve to the same identity at Meta (verified with a live canary in June:
  // adding or removing the 1 changed nothing). So the 1 is a spelling, not a
  // fault.
  if (cc === '52' && national.length === 11 && national[0] === '1') national = national.slice(1);

  // The country code typed twice (+44 44 7956 64033). Checked before length so
  // the host is told the actual cause rather than "too long".
  if (national.startsWith(cc) && !NATIONAL_LENGTHS[cc].includes(national.length)) {
    return { code: 'doubled-country-code', detail: `+${cc} appears twice` };
  }

  const allowed = NATIONAL_LENGTHS[cc];
  if (!allowed.includes(national.length)) {
    return {
      code: 'wrong-length',
      detail: `${national.length} digits after +${cc}, expected ${allowed.join(' or ')}`
    };
  }

  const mobile = MOBILE_RULES[cc];
  if (mobile && mobile.startsWith.some(p => national.startsWith(p))
      && national.length !== mobile.length) {
    return {
      code: 'wrong-length',
      detail: `a +${cc} mobile has ${mobile.length} digits, this has ${national.length}`
    };
  }

  // The last two of the six: a well-formed number for the WRONG country. Only
  // answerable with a hint, and only claimed when the national part would be a
  // valid number in the expected country AND is not valid where it says it is.
  // Stated as a question ("looks like…"), never as a fact.
  if (expectedCountry && expectedCountry !== cc) {
    const expLengths = NATIONAL_LENGTHS[expectedCountry];
    const expMobile = MOBILE_RULES[expectedCountry];
    const fitsExpected = expLengths && expLengths.includes(national.length)
      && (!expMobile || !expMobile.startsWith.some(p => national.startsWith(p))
          || national.length === expMobile.length);
    // Only interesting when the national part looks like a MOBILE where the
    // event is — a landline-shaped coincidence is not worth a warning.
    const looksLikeExpectedMobile = fitsExpected && expMobile
      && expMobile.startsWith.some(p => national.startsWith(p))
      && national.length === expMobile.length;
    if (looksLikeExpectedMobile) {
      return {
        code: 'unexpected-country',
        detail: `+${cc}, but the digits look like a +${expectedCountry} mobile`
      };
    }
  }

  return null;
}

/** Convenience: is this number provably mis-shaped? */
function isMalformedPhone(phone, expectedCountry) {
  return phoneShapeProblem(phone, expectedCountry) !== null;
}

const PhoneShape = { phoneShapeProblem, isMalformedPhone, NATIONAL_LENGTHS };

// Dual-environment, matching assets/js/utils/guest-counts.js: the browser gets
// the globals, the server requires this same file. One implementation — a
// second copy is how the surfaces drifted apart in the first place.
if (typeof window !== 'undefined') {
  window.PhoneShape = PhoneShape;
  window.phoneShapeProblem = phoneShapeProblem;
}
if (typeof module !== 'undefined' && module.exports) module.exports = PhoneShape;
